import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

const schema = z.object({
  checkin: z.string().min(1, "Sila pilih tarikh masuk"),
  checkout: z.string().min(1, "Sila pilih tarikh keluar"),
  full_name: z.string().min(2, "Sila masukkan nama penuh"),
  phone: z
    .string()
    .regex(/^(\+?6?01)[0-9]{7,9}$/, "Format: 012xxxxxxx atau +6012xxxxxxx"),
  email: z.string().email("E-mel tidak sah").optional().or(z.literal("")),
  adults: z.number().int().min(1).max(20),
  children: z.number().int().min(0).max(20),
  special_requests: z.string().max(1000).optional(),
  promo_code: z.string().max(40).optional(),
  policy_agreed: z.literal(true, {
    errorMap: () => ({ message: "Sila bersetuju dengan polisi sebelum meneruskan" }),
  }),
});

type FormValues = z.infer<typeof schema>;

export default function BookingForm() {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { adults: 2, children: 0 },
  });

  const checkin = watch("checkin");
  const checkout = watch("checkout");

  const availability = useQuery({
    queryKey: ["availability", checkin, checkout],
    queryFn: async () => {
      const res = await fetch(`/api/v1/availability?checkin=${checkin}&checkout=${checkout}`);
      if (!res.ok) throw new Error("Ralat menyemak ketersediaan");
      return res.json() as Promise<{
        available: boolean;
        nights: number;
        total_cents: number;
        max_guests: number;
      }>;
    },
    enabled: Boolean(checkin && checkout && checkout > checkin),
  });

  const createBooking = useMutation({
    mutationFn: async (values: FormValues) => {
      // NOTE: Turnstile widget renders here in production and supplies this token.
      // Placeholder token below is rejected server-side outside of a test/dev secret.
      const turnstile_token = (window as any).__turnstileToken ?? "";
      const res = await fetch("/api/v1/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, turnstile_token }),
      });
      const data = (await res.json()) as { reference: string; total_cents: number; nights: number; expires_at: string; error?: string };

      if (!res.ok) throw new Error(data.error ?? "Ralat membuat tempahan");
      return data as { reference: string; total_cents: number; nights: number; expires_at: string };
    },
    onSuccess: (data, variables) => {
      navigate(`/tempahan/ringkasan/${data.reference}`, {
        state: { ...data, checkin: variables.checkin, checkout: variables.checkout },
      });
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  const onSubmit = (values: FormValues) => {
    setSubmitError(null);
    createBooking.mutate(values);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl p-6 shadow-sm space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tarikh Masuk</label>
          <input type="date" {...register("checkin")} className="w-full border rounded-lg px-3 py-2" />
          {errors.checkin && <p className="text-red-600 text-xs mt-1">{errors.checkin.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tarikh Keluar</label>
          <input type="date" {...register("checkout")} className="w-full border rounded-lg px-3 py-2" />
          {errors.checkout && <p className="text-red-600 text-xs mt-1">{errors.checkout.message}</p>}
        </div>
      </div>

      {checkin && checkout && checkout > checkin && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            availability.data?.available === false
              ? "bg-red-50 text-red-700"
              : "bg-turquoise-100 text-sea-700"
          }`}
        >
          {availability.isLoading && "Menyemak ketersediaan..."}
          {availability.data?.available === true &&
            `Tersedia — ${availability.data.nights} malam, jumlah RM${(availability.data.total_cents / 100).toFixed(2)}`}
          {availability.data?.available === false &&
            "Maaf, tarikh ini tidak tersedia. Sila pilih tarikh lain."}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nama Penuh</label>
          <input {...register("full_name")} className="w-full border rounded-lg px-3 py-2" />
          {errors.full_name && <p className="text-red-600 text-xs mt-1">{errors.full_name.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">No. Telefon</label>
          <input {...register("phone")} placeholder="012-3456789" className="w-full border rounded-lg px-3 py-2" />
          {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone.message}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">E-mel (pilihan)</label>
        <input {...register("email")} className="w-full border rounded-lg px-3 py-2" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Dewasa</label>
          <input
            type="number"
            {...register("adults", { valueAsNumber: true })}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Kanak-kanak</label>
          <input
            type="number"
            {...register("children", { valueAsNumber: true })}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Kod Promosi (jika ada)</label>
        <input {...register("promo_code")} className="w-full border rounded-lg px-3 py-2" />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Permintaan Khas</label>
        <textarea {...register("special_requests")} className="w-full border rounded-lg px-3 py-2" rows={3} />
      </div>

      {/* TODO: mount real Cloudflare Turnstile widget here (sets window.__turnstileToken) */}
      <div className="text-xs text-sea-500 italic">[Turnstile widget akan dipasang di sini]</div>

      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" {...register("policy_agreed")} className="mt-1" />
        <span>
          Saya bersetuju dengan polisi tempahan (tiada deposit, boleh batal bila-bila masa).
        </span>
      </label>
      {errors.policy_agreed && <p className="text-red-600 text-xs">{errors.policy_agreed.message}</p>}

      {submitError && <p className="text-red-600 text-sm">{submitError}</p>}

      <button
        type="submit"
        disabled={createBooking.isPending || availability.data?.available === false}
        className="w-full bg-sea-500 text-white rounded-lg py-3 font-medium hover:bg-sea-600 transition disabled:opacity-50"
      >
        {createBooking.isPending ? "Memproses..." : "Hantar Tempahan"}
      </button>
    </form>
  );
}
